import apiClient from "./client";
import { displayVersionLabel } from "@/utils/materialVersion";

/** 资料列表查询参数 */
export interface MaterialListParams {
  pageNumber?: number;
  pageSize?: number;
  title?: string;
  primaryCategory?: string;
  category?: string;
  modelCategory?: string;
  confidential?: number;
}

/** 资料列表响应 */
export interface MaterialListResult {
  list: MaterialItem[];
  total: number;
  pageNumber: number;
  pageSize: number;
}

/** 资料条目（与 AdminMaterial 对应） */
export interface MaterialItem {
  id: string;
  title: string;
  type: string;
  pptType?: string;
  primaryCategory?: string;
  category: string;
  modelCategory?: string;
  fileSize: string;
  uploadDate: number | string;
  uploadedBy: string;
  uploadedByName?: string;
  views: number;
  downloads: number;
  previewAllowed: boolean;
  downloadAllowed: boolean;
  downloadWatermark?: boolean;
  thumbnail?: string;
  isVideo: boolean;
  videoDuration?: string;
  fileUrl?: string;
  description?: string;
  tags: string[];
  confidential: boolean;
  confidentialAgents: string[];
  /** 当前版本号（手动输入，如 v1、v1.0） */
  currentVersion?: string;
}

/** 后端返回的原始材料条目（字段全小写） */
export interface RawMaterialItem {
  id: string;
  title: string;
  type: string;
  ppttype?: string;
  primarycategory?: string;
  category: string;
  modelcategory?: string;
  filesize?: string;
  uploaddate?: number;
  uploadedby?: string;
  uploadedbyname?: string;
  views?: number;
  downloads?: number;
  previewallowed?: number;
  downloadallowed?: number;
  downloadwatermark?: number;
  thumbnail?: string;
  isvideo?: number;
  videoduration?: string;
  fileurl?: string;
  description?: string;
  tags?: string | string[];
  confidential?: number;
  confidentialagents?: string | string[];
  currentversion?: string;
}

/** 将后端小写字段转换为前端 camelCase */
export function normalizeItem(raw: RawMaterialItem): MaterialItem {
  return {
    id: raw.id,
    title: raw.title,
    type: raw.type,
    pptType: raw.ppttype,
    primaryCategory: raw.primarycategory,
    category: raw.category,
    modelCategory: raw.modelcategory,
    fileSize: raw.filesize ?? '',
    uploadDate: typeof raw.uploaddate === 'number' ? raw.uploaddate : (raw.uploaddate ?? ''),
    uploadedBy: raw.uploadedby ?? '',
    uploadedByName: raw.uploadedbyname,
    views: raw.views ?? 0,
    downloads: raw.downloads ?? 0,
    previewAllowed: raw.previewallowed === 1,
    downloadAllowed: raw.downloadallowed === 1,
    downloadWatermark: raw.downloadwatermark !== 0,
    thumbnail: raw.thumbnail || undefined,
    isVideo: raw.isvideo === 1,
    videoDuration: raw.videoduration,
    fileUrl: raw.fileurl,
    description: raw.description,
    tags: typeof raw.tags === 'string' ? raw.tags.split(',').map((t) => t.trim()).filter(Boolean) : (Array.isArray(raw.tags) ? raw.tags : []),
    confidential: raw.confidential === 1,
    confidentialAgents: typeof raw.confidentialagents === 'string' ? raw.confidentialagents.split(',').map((a) => a.trim()).filter(Boolean) : (Array.isArray(raw.confidentialagents) ? raw.confidentialagents : []),
    currentVersion: displayVersionLabel(raw.currentversion),
  };
}

/** 后端 listMaterialVersions 返回的原始版本条目（全小写 key） */
export interface RawMaterialVersionItem {
  versionno: string;
  fileurl: string;
  filesize: string;
  thumbnail?: string;
  videoduration?: string;
  uploadedby: string;
  uploadedbyname?: string;
  uploaddate: number;
  iscurrent: number;
  remark?: string;
}

/** 前端使用的版本条目（camelCase） */
export interface MaterialVersionItem {
  versionNo: string;
  fileUrl: string;
  fileSize: string;
  thumbnail?: string;
  videoDuration?: string;
  uploadedBy: string;
  uploadedByName?: string;
  uploadDate: number;
  isCurrent: boolean;
  remark?: string;
}

export function normalizeVersionItem(raw: RawMaterialVersionItem): MaterialVersionItem {
  return {
    versionNo: displayVersionLabel(raw.versionno),
    fileUrl: raw.fileurl,
    fileSize: raw.filesize ?? '',
    thumbnail: raw.thumbnail || undefined,
    videoDuration: raw.videoduration,
    uploadedBy: raw.uploadedby ?? '',
    uploadedByName: raw.uploadedbyname || undefined,
    uploadDate: raw.uploaddate,
    isCurrent: raw.iscurrent === 1,
    remark: raw.remark,
  };
}

/** 分页查询资料列表 */
export async function listMaterial(params: MaterialListParams): Promise<MaterialListResult> {
  const p = new URLSearchParams();
  if (params.pageNumber) p.append("pageNumber", String(params.pageNumber));
  if (params.pageSize) p.append("pageSize", String(params.pageSize));
  if (params.title) p.append("title", params.title);
  if (params.primaryCategory && params.primaryCategory !== "all") p.append("primaryCategory", params.primaryCategory);
  if (params.category && params.category !== "all") p.append("category", params.category);
  if (params.modelCategory && params.modelCategory !== "all") p.append("modelCategory", params.modelCategory);
  if (params.confidential != null) p.append("confidential", String(params.confidential));
  const res = await apiClient.post("/agentmaterialinfo/listMaterial.do", p);
  const d = res.data;
  if (d && d.success) {
    const raw = d.data as { list: RawMaterialItem[]; total: number; pageNumber: number; pageSize: number };
    return { list: (raw.list ?? []).map(normalizeItem), total: raw.total, pageNumber: raw.pageNumber, pageSize: raw.pageSize };
  }
  throw new Error(d?.info || "查询资料列表失败");
}

/** 获取资料详情 */
export async function getMaterial(id: string): Promise<MaterialItem> {
  const p = new URLSearchParams();
  p.append("id", id);
  const res = await apiClient.post("/agentmaterialinfo/getMaterial.do", p);
  const d = res.data;
  if (d && d.success) return normalizeItem(d.data as RawMaterialItem);
  throw new Error(d?.info || "获取资料详情失败");
}

/** 新增资料 */
export async function addMaterial(form: Record<string, string | boolean | number>): Promise<MaterialItem> {
  const p = new URLSearchParams();
  Object.entries(form).forEach(([k, v]) => {
    if (v != null && v !== "") p.append(k, String(v));
  });
  const res = await apiClient.post("/agentmaterialinfo/addMaterial.do", p);
  const d = res.data;
  if (d && d.success) return d.data as MaterialItem;
  throw new Error(d?.info || "新增资料失败");
}

/** 更新资料 */
export async function updateMaterial(form: Record<string, string | boolean | number>): Promise<{ id: string; currentVersion?: string }> {
  const p = new URLSearchParams();
  Object.entries(form).forEach(([k, v]) => {
    if (v != null) p.append(k, String(v));
  });
  const res = await apiClient.post("/agentmaterialinfo/updateMaterial.do", p);
  const d = res.data;
  if (d && d.success) {
    const raw = (d.data ?? {}) as Record<string, unknown>;
    return {
      id: String(raw.id ?? raw.ID ?? form.id ?? ""),
      currentVersion: (raw.currentVersion ?? raw.currentversion) != null
        ? displayVersionLabel(String(raw.currentVersion ?? raw.currentversion))
        : undefined,
    };
  }
  throw new Error(d?.info || "更新资料失败");
}

/** 查询资料历史版本（管理端） */
export async function listMaterialVersions(materialId: string): Promise<{
  materialId: string;
  currentVersion: string;
  list: MaterialVersionItem[];
}> {
  const p = new URLSearchParams();
  p.append("materialId", materialId);
  const res = await apiClient.post("/agentmaterialinfo/listMaterialVersions.do", p);
  const d = res.data;
  if (d && d.success) {
    const raw = d.data as { materialid: string; currentversion?: string; list: RawMaterialVersionItem[] };
    return {
      materialId: raw.materialid,
      currentVersion: displayVersionLabel(raw.currentversion),
      list: (raw.list ?? []).map(normalizeVersionItem),
    };
  }
  throw new Error(d?.info || "查询历史版本失败");
}

/** 删除资料 */
export async function deleteMaterial(
  id: string,
  options?: { notify?: boolean }
): Promise<void> {
  const p = new URLSearchParams();
  p.append("id", id);
  if (options?.notify != null) {
    p.append("notify", String(options.notify));
  }
  const res = await apiClient.post("/agentmaterialinfo/deleteMaterial.do", p);
  const d = res.data;
  if (d && d.success) return;
  throw new Error(d?.info || "删除资料失败");
}

/** 切换机密状态 */
export async function toggleConfidential(id: string): Promise<{ id: string; confidential: number }> {
  const p = new URLSearchParams();
  p.append("id", id);
  const res = await apiClient.post("/agentmaterialinfo/toggleConfidential.do", p);
  const d = res.data;
  if (d && d.success) return d.data as { id: string; confidential: number };
  throw new Error(d?.info || "切换机密状态失败");
}

/** 切换下载开关 */
export async function toggleDownload(id: string): Promise<{ id: string; downloadAllowed: boolean }> {
  const p = new URLSearchParams();
  p.append("id", id);
  const res = await apiClient.post("/agentmaterialinfo/toggleDownload.do", p);
  const d = res.data;
  if (d && d.success) return d.data as { id: string; downloadAllowed: boolean };
  throw new Error(d?.info || "切换下载开关失败");
}

/** 上传资料文件 */
export interface UploadFileResult {
  url: string;
  name: string;
  size: number;
  ext: string;
  type: string;
}

export async function uploadMaterialFile(file: File): Promise<UploadFileResult> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await apiClient.post("/agentupload/uploadFile.do", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 0, // 上传不限时，大文件需要较长时间
  });
  const d = res.data;
  if (d && d.success) return d.data as UploadFileResult;
  throw new Error(d?.info || "文件上传失败");
}

/** 保存机密资料授权代理商 */
export async function saveConfidentialAgents(id: string, agentIds: string[]): Promise<void> {
  const p = new URLSearchParams();
  p.append("id", id);
  p.append("agentIds", agentIds.join(","));
  const res = await apiClient.post("/agentmaterialinfo/saveConfidentialAgents.do", p);
  const d = res.data;
  if (d && d.success) return;
  throw new Error(d?.info || "保存授权失败");
}
